// format.js — the ONE home for value presentation (NIGHT_SHIFT.md item 6,
// 2026-07-19: "agree on a way to present cost dollars, and then it's applied
// everywhere without even thinking, even to new items"). Decimals, $
// placement, thousands separators and the empty-value marks are decided HERE
// and nowhere else — see STATUS.md "AGREED FORMATS" for the table this
// implements. Do not re-litigate the table; it is settled.
//
// Presentation only — no DB writes, no cost-engine math lives here.
//
//   type          rule                                            example
//   money         $ hard left, comma thousands, exactly 2 decimals  $1,234.56
//   rate          money, then the unit ALWAYS attached              $42.00/hr
//   percent       stored value is the MULTIPLIER; shown/typed as %  1.11 <-> 111%
//   qty           comma thousands, 0 decimals                      5,470 kg
//   qtyDetail     comma thousands, up to 3 decimals                52,007.2 kg
//                 (same column carries 0.96 and 52,007 — see below)
//   labor_productivity  comma thousands, 1 decimal (a TYPE, never a       13.2 kg/hr
//                 magnitude threshold — see catalog_lines.output_rate)

const _fmtMoney = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const _fmtQty = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });
const _fmtProductivity = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

// CENTS ARE FOR UNIT PRICES ONLY (Alex, 2026-08-14: "only drop the two digit unless
// it is unit price"). An extended amount's cents are noise that costs three
// characters in a narrow column; a unit price's cents ARE the number.
//
//   money(1234.56)      -> "$1,235"      an extended amount
//   moneyUnit(5.5)      -> "$5.50"       a unit price
//   rate(42, 'hr')      -> "$42.00/hr"   a unit price with its unit
const _fmtMoneyWhole = new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD',
  minimumFractionDigits: 0, maximumFractionDigits: 0,
});

// money: an EXTENDED amount -> "$1,235". No cents.
export function money(n) {
  if (n == null) return '';
  const num = Number(n);
  if (!Number.isFinite(num)) return '';
  return _fmtMoneyWhole.format(num);
}

// moneyUnit: a UNIT price -> "$5.50". Keeps cents, because rounding a unit price
// changes every quantity computed from it.
export function moneyUnit(n) {
  if (n == null) return '';
  const num = Number(n);
  if (!Number.isFinite(num)) return '';
  return _fmtMoney.format(num);
}

// rate: a unit price + its unit, ALWAYS attached (never move the unit into the
// column header instead) -> "$42.00/hr", "$5.50/kg". A rate is a unit price, so it
// keeps its cents.
export function rate(n, unit) {
  if (n == null) return '';
  const m = moneyUnit(n);
  if (!m) return '';
  return unit ? `${m}/${unit}` : m;
}

// percent: the stored value is the MULTIPLIER (1.11); displayed/typed as a
// percent (111%). 0.1%-precision, trailing zero dropped — 1.11 -> "111%",
// 1.111 -> "111.1%" (matches the net_to_gross cell this was extracted from).
export function percent(n) {
  if (n == null) return '';
  const num = Number(n);
  if (!Number.isFinite(num)) return '';
  const pct = Math.round(num * 1000) / 10;
  return `${pct}%`;
}

// percentToMultiplier: parses a typed "111%" / "111" back to the stored
// multiplier (1.11) — the editor round-trip. Returns null on a non-numeric
// edit so the caller can reject rather than silently store NaN.
export function percentToMultiplier(text) {
  const pct = parseFloat(String(text).replace('%', '').trim());
  return Number.isFinite(pct) ? pct / 100 : null;
}

// multiplierToPercent: seeds an editor with the percent NUMBER (not string) a
// cell already shows — same rounding as percent() minus the '%'.
export function multiplierToPercent(n) {
  if (n == null) return null;
  const num = Number(n);
  return Number.isFinite(num) ? Math.round(num * 1000) / 10 : null;
}

// qtyDetail: comma thousands, up to 3 decimals -> "52,007.2 kg", "0.96 m2".
//
// Alex, 2026-08-12, on the customer screen: "numbers like kilograms that have no
// space and are 3-digits! I thought that we managed all the formats to be always
// consistent." He is right — that screen was printing `{qty}` raw, so 52007.2
// came out ungrouped. This is the ONE home for that, per the header.
//
// ⚠ WHY NOT `qty` ABOVE: it is deliberately 0-decimal, which is correct for
// "5,470 kg" and destroys "0.96 m2" — the same trap that had the Product
// mapping preview reporting that 0.75 resolves to 1 (STATUS, 2026-08-03). A
// scope list carries both magnitudes in the same column, so it needs grouping
// AND decimals; 3 is where this catalog's real quantities stop being meaningful.
const _fmtQtyDetail = new Intl.NumberFormat('en-US', { maximumFractionDigits: 3 });
export function qtyDetail(n, unit) {
  if (n == null) return '';
  const num = Number(n);
  if (!Number.isFinite(num)) return '';
  const s = _fmtQtyDetail.format(num);
  return unit ? `${s} ${unit}` : s;
}

// qty: comma thousands, 0 decimals -> "5,470", or "5,470 kg" with a unit.
export function qty(n, unit) {
  if (n == null) return '';
  const num = Number(n);
  if (!Number.isFinite(num)) return '';
  const s = _fmtQty.format(num);
  return unit ? `${s} ${unit}` : s;
}

// labor_productivity: comma thousands, 1 decimal, ALWAYS — this is a TYPE
// (catalog_lines.output_rate: kg per labour-hour), never a magnitude threshold,
// so it never drops to 0 decimals as the value grows. -> "13.2 kg/hr"
export function labor_productivity(n, unit) {
  if (n == null) return '';
  const num = Number(n);
  if (!Number.isFinite(num)) return '';
  const s = _fmtProductivity.format(num);
  return unit ? `${s} ${unit}` : s;
}
